import React from 'react'

import { EmployeeInvesting } from './components/EmployeeInvesting'
import { EmployeeDebtLevels } from './components/EmployeeDebtLevels'
import { EmployeeSpending } from './components/EmployeeSpending'
import { EmployeeFinancialHealthAreas } from './components/EmployeeFinancialHealthAreas'
import { FinancialHealthHeatmapByPercentage } from './components/FinancialHealthHeatmapByPercentage'
import { FinancialHealthHeatmapByCount } from './components/FinancialHealthHeatmapByCount'
import { FinancialDistributionGroup } from './components/FinancialDistributionGroup'

import { Text } from '@UILibrary/Text'
import { Stack } from '@UILibrary/Stack'

interface ChartData {
  labels: string[]
  datasets: object
}

interface FinancialHealthData {
  financial_health_distribution: {
    labels: string[]
    datasets: any[]
  }
  financial_health_distribution_by_count: {
    labels: string[]
    datasets: any[]
  }
  cohort_financial_health: {
    labels: string[]
    names: string[]
    datasets: any[]
  }
  income_breakdown: {
    debt_percentage: number
    expenses_percentage: number
    investments_percentage: number
    remaining_percentage: number
    biggest_contribution_text: string
  }
  areas_of_improvement: {
    employee_debt_levels_status: any
    employee_investing_status: any
    employee_spending_status: any
  }
  debt_repayments_as_a_percentage_of_gross_income: {
    current_percentage: number
    benchmark: number
    benchmark_variance_percentage: number
    scope_variance_percentage: number
    chart_data: ChartData
  }
  expenses_as_a_percentage_of_gross_income: {
    current_percentage: number
    benchmark: number
    benchmark_variance_percentage: number
    scope_variance_percentage: number
    chart_data: ChartData
  }
  investment_contribution_as_a_percentage_of_gross_income: {
    current_percentage: number
    benchmark: number
    benchmarkVariancePercentage: number
    scope_variance_percentage: number
    chart_data: ChartData
  }
  asset_type_distribution: object[]
}

interface FinancialHealthProps {
  financial_health: FinancialHealthData
  selectedPeriod: string
}

export const FinancialHealth: React.FC<FinancialHealthProps> = ({ financial_health, selectedPeriod }) => {
  return (
    <div>
      <Text heading2b as="h1" colour="default" margin={{ bottom: 'l' }}>
        Your team's financial health
      </Text>

      <Stack horizontal spacing="m">
        <FinancialHealthHeatmapByPercentage
          financialHealthDistribution={financial_health.financial_health_distribution}
        />

        <FinancialHealthHeatmapByCount
          financialHealthDistributionByCount={financial_health.financial_health_distribution_by_count}
        />
      </Stack>

      <FinancialDistributionGroup
        cohortFinancialHealth={financial_health.cohort_financial_health}
        incomeBreakdown={financial_health.income_breakdown}
      />

      <EmployeeFinancialHealthAreas
        employeeDebtLevelsStatus={financial_health.areas_of_improvement.employee_debt_levels_status}
        employeeInvestingStatus={financial_health.areas_of_improvement.employee_investing_status}
        employeeSpendingStatus={financial_health.areas_of_improvement.employee_spending_status}
      />

      <EmployeeDebtLevels
        currentDebtPercentage={financial_health.debt_repayments_as_a_percentage_of_gross_income.current_percentage}
        benchmark={financial_health.debt_repayments_as_a_percentage_of_gross_income.benchmark}
        benchmarkVariancePercentage={financial_health.debt_repayments_as_a_percentage_of_gross_income.benchmark_variance_percentage}
        scopeVariancePercentage={financial_health.debt_repayments_as_a_percentage_of_gross_income.scope_variance_percentage}
        timeframe={selectedPeriod}
        chartData={financial_health.debt_repayments_as_a_percentage_of_gross_income.chart_data}
      />

      <EmployeeSpending
        currentSpendingPercentage={financial_health.expenses_as_a_percentage_of_gross_income.current_percentage}
        benchmark={financial_health.expenses_as_a_percentage_of_gross_income.benchmark}
        benchmarkVariancePercentage={financial_health.expenses_as_a_percentage_of_gross_income.benchmark_variance_percentage}
        scopeVariancePercentage={financial_health.expenses_as_a_percentage_of_gross_income.scope_variance_percentage}
        timeframe={selectedPeriod}
        chartData={financial_health.expenses_as_a_percentage_of_gross_income.chart_data}
      />

      <EmployeeInvesting
        currentInvestmentPercentage={financial_health.investment_contribution_as_a_percentage_of_gross_income.current_percentage}
        benchmark={financial_health.investment_contribution_as_a_percentage_of_gross_income.benchmark}
        benchmarkVariancePercentage={financial_health.investment_contribution_as_a_percentage_of_gross_income.benchmarkVariancePercentage}
        scopeVariancePercentage={financial_health.investment_contribution_as_a_percentage_of_gross_income.scope_variance_percentage}
        timeframe={selectedPeriod}
        chartData={financial_health.investment_contribution_as_a_percentage_of_gross_income.chart_data}
        assetTypeDistribution={financial_health.asset_type_distribution}
      />

    </div>
  )
}
